import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult, useInfiniteQuery } from 'react-query'
import { QueryKeyT } from 'src/api/reactQuery'
import { fetchData } from 'src/axios'
import { IBaseEntity } from 'src/generated-types'
import { PaginatedResultList } from 'src/generated-types/paginated-result-list'
import { queryClient } from 'src/query-client'

interface UseInfiniteScrollOptions<T> {
  queryUrl: string
  queryParams?: object
  pageSize?: number
  initialPage?: number
  generateQueryKey?: (page: number, pageSize: number) => QueryKeyT | undefined
  enabled?: boolean
}

interface UseInfiniteScrollReturn<T> {
  data: T[]
  isLoading: boolean
  isFetching: boolean
  hasMore: boolean
  loadMore: () => void
  reset: () => void
  setData: React.Dispatch<React.SetStateAction<T[]>>
  totalCount: number
  refetch: () => void
  getItemPageInfo: (item: T) => { pageNumber: number; positionInPage: number; itemIndex: number } | null
  invalidateSpecificPages: (pageNumbers: number[]) => void
  invalidateFromPage: (startPage: number) => void
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<PaginatedResultList<T>, unknown>>
  hasNextPage: boolean | undefined
  isFetchingNextPage: boolean
}

export const useInfiniteScroll = <T extends IBaseEntity>({
  queryUrl,
  queryParams = {},
  pageSize = 20,
  initialPage = 0,
  generateQueryKey,
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> => {
  const [data, setData] = useState<T[]>([])
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [hasMore, setHasMore] = useState(true)

  const queryParamsRef = useRef(queryParams)

  useEffect(() => {
    queryParamsRef.current = queryParams
  }, [queryParams])

  const {
    data: serverData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery<PaginatedResultList<T>>({
    queryKey: generateQueryKey
      ? generateQueryKey(currentPage, pageSize)
      : [queryUrl, { ...queryParams, page: currentPage, pageSize }],
    queryFn: ({ pageParam = 0 }) =>
      fetchData<PaginatedResultList<T>>(queryUrl, { page: pageParam, pageSize, ...queryParamsRef.current }),
    getNextPageParam: lastPage => {
      return lastPage.page + 1 < Math.ceil(lastPage.totalCount / pageSize) ? lastPage.page + 1 : undefined
    },
    // Keep showing the previously loaded page while the next query (e.g. after a
    // filter change) is in flight. Without this, isLoading flips to true on every
    // query-key change and consumers that swap to a spinner will unmount the table —
    // which destroys any focused filter inputs inside the header row.
    keepPreviousData: true,
  })

  const totalCount = useMemo(() => {
    if (serverData?.pages && serverData.pages.length > 0) {
      return serverData.pages[0].totalCount
    }
    return 0
  }, [serverData, isLoading, isFetching])

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      setCurrentPage(prev => prev + 1)
    }
  }, [hasMore, isLoading])

  const reset = useCallback(() => {
    setData([])
    setCurrentPage(0)
    setHasMore(true)
  }, [])

  const getItemPageInfo = useCallback(
    (item: T) => {
      const itemIndex = data.findIndex(d => d.id === item.id)
      if (itemIndex === -1) return null

      const pageNumber = Math.floor(itemIndex / pageSize)
      const positionInPage = itemIndex % pageSize

      return {
        pageNumber,
        positionInPage,
        itemIndex,
      }
    },
    [data, pageSize],
  )

  // Invalidate only the specified pages in React Query cache
  const invalidateSpecificPages = useCallback(
    (pageNumbers: number[]) => {
      pageNumbers.forEach(pageNum => {
        const queryKey = generateQueryKey
          ? generateQueryKey(pageNum, pageSize)
          : [queryUrl, { ...queryParams, page: pageNum, pageSize }]
        if (queryKey) {
          queryClient.invalidateQueries({
            queryKey,
            exact: false,
          })
        }
      })
    },
    [generateQueryKey, pageSize, queryUrl, queryParams],
  )

  // Invalidate from a specific page onwards
  const invalidateFromPage = useCallback(
    (startPage: number) => {
      const totalPages = Math.ceil(totalCount / pageSize)
      const pagesToInvalidate = Array.from({ length: totalPages - startPage }, (_, i) => startPage + i)
      invalidateSpecificPages(pagesToInvalidate)
    },
    [invalidateSpecificPages, totalCount, pageSize],
  )

  return {
    data: serverData ? serverData.pages.flatMap(page => page.items) : [],
    isLoading,
    isFetching,
    hasMore,
    loadMore,
    reset,
    setData,
    totalCount,
    refetch,
    getItemPageInfo,
    invalidateSpecificPages,
    invalidateFromPage,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}
